import { useUser } from '@client/hooks/business/user/useUser';
import type { Role } from '@shared/constants/business/user';
import type { Permission } from '@shared/permissions/permissions';
import { hasPermission, ROLE_PERMISSIONS } from '@shared/permissions/roles';
import { useCallback, useMemo } from 'react';

/**
 * Centralized hook for permission checking
 *
 * @example
 * ```tsx
 * const { can, cannot, checkAll, checkAny } = usePermissions();
 *
 * if (can('claim:create')) {
 *   // User can create claims
 * }
 *
 * if (cannot('user:delete')) {
 *   // User cannot delete users
 * }
 *
 * const hasAccess = checkAny(['claim:read', 'claim:update']);
 * ```
 */
export function usePermissions() {
    const user = useUser({ throwOnUndefined: false });

    /**
     * Check if user has a specific permission
     */
    const can = useCallback(
        (permission: Permission): boolean => {
            if (!user) return false;
            return hasPermission(user.roles, permission);
        },
        [user],
    );

    /**
     * Check if user does NOT have a specific permission
     */
    const cannot = useCallback(
        (permission: Permission): boolean => {
            return !can(permission);
        },
        [can],
    );

    /**
     * Check if user has ANY of the provided permissions
     * Returns true if user has at least one permission
     */
    const checkAny = useCallback(
        (permissions: Permission[]): boolean => {
            if (!user) return false;
            return permissions.some((permission) => hasPermission(user.roles, permission));
        },
        [user],
    );

    /**
     * Check if user has ALL of the provided permissions
     * Returns true only if user has every permission
     */
    const checkAll = useCallback(
        (permissions: Permission[]): boolean => {
            if (!user) return false;
            return permissions.every((permission) => hasPermission(user.roles, permission));
        },
        [user],
    );

    /**
     * Check if user has a specific role
     */
    const hasRole = useCallback(
        (role: Role): boolean => {
            if (!user) return false;
            return user.roles.includes(role);
        },
        [user],
    );

    /**
     * Check if user is an admin (org_admin or super_admin)
     */
    const isAdmin = useMemo(() => {
        if (!user) return false;
        return hasPermission(user.roles, 'org:admin') || hasPermission(user.roles, 'super:admin');
    }, [user]);

    /**
     * Check if user is a super admin
     */
    const isSuperAdmin = useMemo(() => {
        return hasPermission(user?.roles ?? [], 'super:admin');
    }, [user]);

    /**
     * Get all permissions for the current user based on their roles
     */
    const getAllPermissions = useCallback((): Permission[] => {
        if (!user) return [];

        const allPermissions = new Set<Permission>();

        user.roles.forEach((role) => {
            const rolePermissions = ROLE_PERMISSIONS[role] || [];
            rolePermissions.forEach((permission: Permission) => {
                allPermissions.add(permission);
            });
        });

        return Array.from(allPermissions);
    }, [user]);

    return {
        // Permission checking
        can,
        cannot,
        checkAny,
        checkAll,

        // Role checking
        hasRole,
        isAdmin,
        isSuperAdmin,

        // Utility
        getAllPermissions,

        // Raw user for edge cases
        user,
    };
}

/**
 * Hook to check if user has permission to perform an action on an entity
 * Handles ownership and handling party based permissions
 */
export function useEntityPermissions<T extends { ownerId?: bigint | null; handlingPartyIds?: bigint[] | null }>(
    entity: T | undefined,
) {
    const user = useUser({ throwOnUndefined: false });
    const { can } = usePermissions();

    const isOwner = useMemo(() => {
        if (!entity || !user) return false;
        return entity.ownerId === user.id;
    }, [entity, user]);

    const isInHandlingParty = useMemo(() => {
        if (!entity || !user) return false;
        if (!entity.handlingPartyIds || !user.handlingPartyIds) return false;

        return entity.handlingPartyIds.some((partyId) => user.handlingPartyIds.includes(partyId));
    }, [entity, user]);

    /**
     * Check if user can perform an action based on ownership/handling party permissions.
     *
     * Checks permissions in the following hierarchy (returns true on first match):
     * 1. Base permission (e.g., `claim:update`) - Full access
     * 2. Owner permission (e.g., `claim:update:is_owner`) - User is the entity owner
     * 3. Handling party permission (e.g., `claim:update:own_handling_parties`) - User is in entity's handling parties
     * 4. Combined permission (e.g., `claim:update:own_handling_parties_or_is_owner`) - Either owner OR in handling parties
     * 5. Owner without handling parties (e.g., `claim:update:owner_without_handling_parties`) - Owner AND entity has no handling parties
     *
     * @param basePermission - The base permission string without qualifiers (e.g., 'claim:update', 'incident:read')
     * @returns True if user has any applicable permission for the action on this entity
     *
     * @example
     * ```tsx
     * const { canPerformAction } = useEntityPermissions(claim);
     *
     * if (canPerformAction('claim:update')) {
     *   // User can update this claim via one of the permission levels
     * }
     * ```
     */
    const canPerformAction = useCallback(
        (basePermission: string): boolean => {
            if (!user) return false;

            // Check standard permission first
            if (can(`${basePermission}` as Permission)) return true;

            // Check owner-specific permission
            if (isOwner && can(`${basePermission}:is_owner` as Permission)) return true;

            // Check handling party permission
            if (isInHandlingParty && can(`${basePermission}:own_handling_parties` as Permission)) return true;

            // Check combined permission
            if (
                (isOwner || isInHandlingParty) &&
                can(`${basePermission}:own_handling_parties_or_is_owner` as Permission)
            ) {
                return true;
            }

            // Special case for owner without handling parties
            if (
                isOwner &&
                !entity?.handlingPartyIds?.length &&
                can(`${basePermission}:owner_without_handling_parties` as Permission)
            ) {
                return true;
            }

            return false;
        },
        [user, can, isOwner, isInHandlingParty, entity],
    );

    return {
        isOwner,
        isInHandlingParty,
        canPerformAction,
    };
}
